/**
 * Модель для работы с инфраструктурой
 */

import { createModel } from '@rematch/core';
import { RootModel } from './index';

import { IShortUser, INotificationType, INotificationTarget, IEvent } from './types';
import setStartEndOfDay from '../utils/setStartEndOfDay';

import NotificationsService from '../services/NotificationsService';
import UsersService from '../services/UsersService';

interface IData {
  users: IShortUser[];
  notificationTypes: INotificationType[];
  notificationTargets: INotificationTarget[];
}

interface IList {
  data: IEvent[] | null;
  pagesCount: number;
}

interface IState {
  data: IData;
  list: IList;
}

const initialState: IState = {
  data: {
    users: [],
    notificationTypes: [],
    notificationTargets: []
  },
  list: {
    data: null,
    pagesCount: 0
  }
};

// Staleness guard for list requests: only the most recent request may commit its
// result. reset() bumps this so an in-flight request that resolves after unmount
// cannot write stale rows back into the store.
let listSeq = 0;

// Shared fetch+commit guarded by listSeq: an out-of-order or superseded request
// (a newer filter/page change, or reset on unmount) is dropped instead of
// clobbering the current list.
const fetchList = async (params: any, commit: (data: IList) => void) => {
  const seq = ++listSeq;
  const cloneParams = setStartEndOfDay(params, ['from', 'to']);

  try {
    const { data } = await NotificationsService.getAll(cloneParams);

    if (seq !== listSeq) return;

    commit(data);
  } catch (error) {
    return error;
  }
};

export const notifications = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setData(state: IState, payload: IData): IState {
      return { ...state, data: payload };
    },
    setList(state: IState, payload: IList): IState {
      return { ...state, list: payload };
    },
    reset(): IState {
      listSeq++;
      return { ...initialState };
    }
  },
  effects: () => ({
    async getData() {
      try {
        const [
          { data: users },
          { data: notificationTypes },
          { data: notificationTargets }
        ] = await Promise.all([
          UsersService.getAllEmployeesShort(),
          NotificationsService.getTypesAll(),
          NotificationsService.getTargetsAll()
        ]);

        this.setData({ users, notificationTypes, notificationTargets });
      } catch (error) {
        return error;
      }
    },
    async getList(params: any) {
      return fetchList(params, (data) => this.setList(data));
    },
    // Background refresh triggered by a SignalR event. Intentionally a separate
    // effect from getList: @rematch/loading tracks it under its own key
    // (loading.effects.notifications.reloadList) and therefore does NOT flip
    // loadingEffects.notifications.getList, which other components read for
    // their own spinner — keeping the refresh silent store-wide. On failure it
    // returns the error silently; the current list is left in place.
    async reloadList(params: any) {
      return fetchList(params, (data) => this.setList(data));
    }
  })
});
